import {
  CustomDropdown,
  Icon,
  BaseType,
  FlatButton,
} from '@globalmed/atlas-ui';
import { Editor, useEditorState } from '@tiptap/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import style from './TextEditorMenu.module.css';
import { useOutsideClick } from '@/hooks/useOutsideClick';

type TProps = {
  editor: Editor;
};

/**
 * Renders a rich text editor menu with formatting options for headings, paragraphs, and text styles.
 * Provides buttons for bold, italic, strike, underline, bullet list, and ordered list.
 * Integrates with TipTap editor and updates menu state based on current editor selection.
 * @param props - Component props
 * @param props.editor - TipTap Editor instance to control
 * @returns JSX.Element - A toolbar menu for text editing
 */
export const TextEditorMenu = ({ editor }: TProps) => {
  const editorState = useEditorState({
    editor,
    selector: (ctx) => {
      return {
        isBold: ctx.editor.isActive('bold') ?? false,
        isItalic: ctx.editor.isActive('italic') ?? false,
        isStrike: ctx.editor.isActive('strike') ?? false,
        isUnderline: ctx.editor.isActive('underline') ?? false,
        isBulletList: ctx.editor.isActive('bulletList') ?? false,
        isOrderedList: ctx.editor.isActive('orderedList') ?? false,
        isParagraphSmall:
          ctx.editor.isActive('paragraph', { class: 'small' }) ?? false,
        isParagraphDefault:
          ctx.editor.isActive('paragraph', { class: 'default' }) ?? false,
        isParagraphLarge:
          ctx.editor.isActive('paragraph', { class: 'large' }) ?? false,
        isHeading1: ctx.editor.isActive('heading', { level: 1 }) ?? false,
        isHeading2: ctx.editor.isActive('heading', { level: 2 }) ?? false,
        isHeading3: ctx.editor.isActive('heading', { level: 3 }) ?? false,
      };
    },
  });
  const { t } = useTranslation();

  const stylesItems: BaseType[] = [
    {
      id: 'heading-1',
      title: t('editor.heading-level', { level: 1 }),
    },
    {
      id: 'heading-2',
      title: t('editor.heading-level', { level: 2 }),
    },
    {
      id: 'heading-3',
      title: t('editor.heading-level', { level: 3 }),
    },
    {
      id: 'paragraph-small',
      title: t('editor.small'),
    },
    {
      id: 'paragraph-default',
      title: t('common.default'),
    },
    {
      id: 'paragraph-large',
      title: t('editor.large'),
    },
  ];

  const [open, setOpen] = useState(false);
  const [textStyle, setTextStyle] = useState('paragraph-default');
  const textStyleText =
    stylesItems.find((item) => item.id === textStyle)?.title ?? '';

  const ref = useOutsideClick<HTMLDivElement>(() => setOpen(false));

  const handleStyleChange = (val: string) => {
    const [key, value] = val.split('-');
    const level = Number(value);
    const levelIsLevel = level === 1 || level === 2 || level === 3;
    if (key === 'heading' && levelIsLevel) {
      editor.chain().focus().toggleHeading({ level }).run();
    }
    if (key === 'paragraph') {
      editor
        .chain()
        .focus()
        .updateAttributes('paragraph', { class: value })
        .run();
    }
    setTextStyle(val);
    setOpen(false);
  };

  useEffect(() => {
    const {
      isHeading1,
      isHeading2,
      isHeading3,
      isParagraphDefault,
      isParagraphLarge,
      isParagraphSmall,
    } = editorState;
    if (isHeading1 || isHeading2 || isHeading3) {
      const number =
        (isHeading1 && '1') || (isHeading2 && '2') || (isHeading3 && '3');
      setTextStyle(`heading-${number}`);
      return;
    }
    if (isParagraphDefault || isParagraphLarge || isParagraphSmall) {
      const number =
        (isParagraphDefault && 'default') ||
        (isParagraphLarge && 'large') ||
        (isParagraphSmall && 'small');
      setTextStyle(`paragraph-${number}`);
    }
  }, [editorState]);

  return (
    <div className={style.menu}>
      <div className={style.dropdownWrapper} ref={ref}>
        <FlatButton
          className={style.dropdownButton}
          onClick={() => setOpen((pr) => !pr)}
          aria-label={t('editor.text-style')}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span className={style.dropdownButtonText}>{textStyleText}</span>
          <Icon
            name={open ? 'ChevronDown' : 'ChevronUp'}
            size="20px"
            decorative
          />
        </FlatButton>

        {open && (
          <CustomDropdown
            sx={{ width: '200px', bottom: '-16px' }}
            className={style.dropdown}
            selected={textStyle}
            onValueClick={handleStyleChange}
          >
            {stylesItems.map((item) => (
              <CustomDropdown.Item key={item.id} {...item} />
            ))}
          </CustomDropdown>
        )}
      </div>

      <div className={style.separator}></div>

      <div className={style.group}>
        <FlatButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          aria-label={t('editor.bold')}
          aria-pressed={editorState.isBold}
          style={{
            color: editorState.isBold ? 'var(--colors-primary600)' : undefined,
          }}
        >
          <Icon name="EditorBold" size="20px" decorative />
        </FlatButton>

        <FlatButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          aria-label={t('editor.italic')}
          aria-pressed={editorState.isItalic}
          style={{
            color: editorState.isItalic
              ? 'var(--colors-primary600)'
              : undefined,
          }}
        >
          <Icon name="EditorItalic" size="20px" decorative />
        </FlatButton>

        <FlatButton
          onClick={() => editor.chain().focus().toggleStrike().run()}
          aria-label={t('editor.strikethrough')}
          aria-pressed={editorState.isStrike}
          style={{
            color: editorState.isStrike
              ? 'var(--colors-primary600)'
              : undefined,
          }}
        >
          <Icon name="EditorStrike" size="20px" decorative />
        </FlatButton>

        <FlatButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          aria-label={t('editor.underline')}
          aria-pressed={editorState.isUnderline}
          style={{
            color: editorState.isUnderline
              ? 'var(--colors-primary600)'
              : undefined,
          }}
        >
          <Icon name="EditorUnderline" size="20px" decorative />
        </FlatButton>
      </div>

      <div className={style.separator}></div>

      <div className={style.group}>
        <FlatButton
          onClick={() => {
            editor.chain().focus().toggleBulletList().run();
            handleStyleChange('paragraph-default');
          }}
          aria-label={t('editor.bullet-list')}
          aria-pressed={editorState.isBulletList}
          style={{
            color: editorState.isBulletList
              ? 'var(--colors-primary600)'
              : undefined,
          }}
        >
          <Icon name="EditorUnordered" size="20px" decorative />
        </FlatButton>

        <FlatButton
          onClick={() => {
            editor.chain().focus().toggleOrderedList().run();
            handleStyleChange('paragraph-default');
          }}
          aria-label={t('editor.ordered-list')}
          aria-pressed={editorState.isOrderedList}
          style={{
            color: editorState.isOrderedList
              ? 'var(--colors-primary600)'
              : undefined,
          }}
        >
          <Icon name="EditorOrderedFill" size="20px" decorative />
        </FlatButton>
      </div>
    </div>
  );
};
